import { Snackbar, Alert, Box, Typography, IconButton } from '@mui/material';
import { CheckCircle, Error, Warning, Info, Close } from '@mui/icons-material';
import { useEffect, useState } from 'react';

const ModernSnackbar = ({ 
  open, 
  message, 
  severity = 'success', 
  onClose, 
  autoHideDuration = 3000 
}) => {
  const darkMode = JSON.parse(sessionStorage.getItem("darkMode"));
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
    }
  }, [open]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsVisible(false);
    setTimeout(() => onClose && onClose(event, reason), 200);
  };

  const getSeverityConfig = (severity) => {
    const configs = {
      success: {
        icon: <CheckCircle />,
        gradient: darkMode
          ? "linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)"
          : "linear-gradient(135deg, rgba(5, 150, 105, 1) 0%, rgba(16, 185, 129, 1) 100%)",
        borderColor: darkMode ? "rgba(16, 185, 129, 0.3)" : "rgba(5, 150, 105, 0.3)",
        shadowColor: darkMode ? "rgba(16, 185, 129, 0.3)" : "rgba(5, 150, 105, 0.25)",
      },
      error: {
        icon: <Error />,
        gradient: darkMode
          ? "linear-gradient(135deg, rgba(248, 113, 113, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)"
          : "linear-gradient(135deg, rgba(220, 38, 38, 1) 0%, rgba(248, 113, 113, 1) 100%)",
        borderColor: darkMode ? "rgba(248, 113, 113, 0.3)" : "rgba(220, 38, 38, 0.3)",
        shadowColor: darkMode ? "rgba(248, 113, 113, 0.3)" : "rgba(220, 38, 38, 0.25)",
      },
      warning: {
        icon: <Warning />,
        gradient: darkMode
          ? "linear-gradient(135deg, rgba(251, 191, 36, 0.9) 0%, rgba(245, 158, 11, 0.9) 100%)"
          : "linear-gradient(135deg, rgba(245, 158, 11, 1) 0%, rgba(251, 191, 36, 1) 100%)",
        borderColor: darkMode ? "rgba(251, 191, 36, 0.3)" : "rgba(245, 158, 11, 0.3)",
        shadowColor: darkMode ? "rgba(251, 191, 36, 0.3)" : "rgba(245, 158, 11, 0.25)",
      },
      info: {
        icon: <Info />,
        gradient: darkMode
          ? "linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(37, 99, 235, 0.9) 100%)"
          : "linear-gradient(135deg, rgba(37, 99, 235, 1) 0%, rgba(59, 130, 246, 1) 100%)",
        borderColor: darkMode ? "rgba(59, 130, 246, 0.3)" : "rgba(37, 99, 235, 0.3)",
        shadowColor: darkMode ? "rgba(59, 130, 246, 0.3)" : "rgba(37, 99, 235, 0.25)",
      },
    };
    return configs[severity] || configs.info;
  };

  const config = getSeverityConfig(severity);

  return (
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      sx={{
        zIndex: 9999,
        "& .MuiSnackbarContent-root": {
          padding: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "16px 20px",
          borderRadius: "12px",
          backgroundColor: darkMode 
            ? "rgba(31, 41, 55, 0.95)" 
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(20px)",
          border: `1px solid ${config.borderColor}`,
          boxShadow: `
            0 20px 25px -5px rgba(0, 0, 0, ${darkMode ? '0.4' : '0.1'}), 
            0 10px 10px -5px rgba(0, 0, 0, ${darkMode ? '0.2' : '0.04'}),
            0 0 0 1px ${config.borderColor},
            0 4px 20px ${config.shadowColor}
          `,
          minWidth: "320px",
          maxWidth: "500px",
          transform: isVisible ? "translateY(0) scale(1)" : "translateY(-20px) scale(0.95)",
          opacity: isVisible ? 1 : 0,
          transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: config.gradient,
            borderRadius: "12px 12px 0 0",
          },
          "&:hover": {
            transform: isVisible ? "translateY(-2px) scale(1.02)" : "translateY(-20px) scale(0.95)",
            boxShadow: `
              0 25px 50px -12px rgba(0, 0, 0, ${darkMode ? '0.5' : '0.15'}), 
              0 0 0 1px ${config.borderColor},
              0 8px 30px ${config.shadowColor}
            `,
          },
        }}
      >
        {/* Icon with gradient background */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "36px",
            height: "36px",
            borderRadius: "8px",
            background: config.gradient,
            color: "#ffffff",
            flexShrink: 0,
            boxShadow: `0 4px 12px ${config.shadowColor}`,
            "& .MuiSvgIcon-root": {
              fontSize: "18px",
            },
          }}
        >
          {config.icon}
        </Box>

        {/* Message Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{
              color: darkMode ? "rgba(243, 244, 246, 1)" : "rgba(17, 24, 39, 1)",
              fontWeight: "500",
              fontSize: "0.875rem",
              lineHeight: "1.4",
              wordBreak: "break-word",
            }}
          >
            {message?.message || message}
          </Typography>
        </Box>

        {/* Close Button */}
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: darkMode ? "rgba(156, 163, 175, 1)" : "rgba(107, 114, 128, 1)",
            backgroundColor: darkMode 
              ? "rgba(75, 85, 99, 0.2)" 
              : "rgba(243, 244, 246, 0.3)",
            width: "28px",
            height: "28px",
            borderRadius: "6px",
            transition: "all 0.2s ease-in-out",
            flexShrink: 0,
            "&:hover": {
              backgroundColor: darkMode 
                ? "rgba(75, 85, 99, 0.4)" 
                : "rgba(229, 231, 235, 0.5)",
              color: darkMode ? "rgba(243, 244, 246, 1)" : "rgba(17, 24, 39, 1)",
              transform: "scale(1.1)",
            },
            "& .MuiSvgIcon-root": {
              fontSize: "16px",
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>
    </Snackbar>
  );
};

export default ModernSnackbar;
